import { Col, DatePicker, Input, InputNumber, Row, Select, TimePicker } from 'antd';

import type { $RuleBooleanExpressionType } from '../../constants/rules/ruleBooleanExpressionTypes';
import type { $RuleInputSchemaFieldModel } from '../../models/rules/RuleInputSchemaModel';
import ListInput from './../core/ListInput';
import React from 'react';
import { RuleExpressionValueTypeEnum } from '../../enums/rules/RuleExpressionValueTypeEnum';
import { isArray } from 'lodash';
import moment from 'moment';

type Props = {
  expressionType?: $RuleBooleanExpressionType;
  inputField?: $RuleInputSchemaFieldModel;
  onChange: (value: any) => void;
  value?: any;
};

// Create a moment in CST timezone
const CST_TIME = moment().zone('America/Chicago');
// Get offset in seconds (negative means behind UTC)
const OFFSET_SECONDS = CST_TIME.utcOffset() * 60;

function RuleExpressionSecondOperandInput({
  expressionType,
  inputField,
  onChange,
  value,
}: Props): React.ReactElement {
  const options = inputField?.oneOf?.map((value, i) => ({
    value,
    label: inputField.oneOfLabels?.[i] ?? `${value}`,
  }));

  if (!expressionType || !inputField) {
    return <Input disabled value={value} />;
  }

  if (
    (expressionType.secondOperandType === RuleExpressionValueTypeEnum.ARRAYNUMBER ||
      expressionType.secondOperandType === RuleExpressionValueTypeEnum.ARRAYSTRING) &&
    (isArray(value) || value == null)
  ) {
    return (
      <ListInput
        onChange={onChange}
        options={options}
        type={
          expressionType.secondOperandType === RuleExpressionValueTypeEnum.ARRAYNUMBER
            ? 'number'
            : 'text'
        }
        value={value}
      />
    );
  }

  if (expressionType.secondOperandType === RuleExpressionValueTypeEnum.BOOLEAN) {
    return (
      <Select onChange={onChange} value={value}>
        <Select.Option value={false}>False</Select.Option>
        <Select.Option value={true}>True</Select.Option>
      </Select>
    );
  }

  if (options != null) {
    return (
      <Select onChange={onChange} value={value}>
        {options.map(({ label, value }) => (
          <Select.Option key={value} value={value}>
            {label}
          </Select.Option>
        ))}
      </Select>
    );
  }

  if (
    expressionType.secondOperandType === RuleExpressionValueTypeEnum.NUMBER &&
    (inputField.dataPointer.endsWith('Date') ||
      inputField.dataPointer.endsWith('DateAndTime') ||
      inputField.dataPointer.endsWith('DateTime') ||
      inputField.name.endsWith('DateAndTime'))
  ) {
    return (
      <Row gutter={1}>
        <Col span={12}>
          <DatePicker
            className="w-full"
            onChange={(e) => {
              onChange(e?.startOf('day').unix());
            }}
            value={value == null ? undefined : moment.unix(value)}
          />
        </Col>
        <Col span={12}>
          <TimePicker
            className="w-full"
            onChange={(e) => {
              if (e == null) {
                return;
              }

              const cstDateTimeSeconds = value + OFFSET_SECONDS;
              const currentTimeSeconds = cstDateTimeSeconds % (60 * 60 * 24);

              onChange(e.unix() + value - currentTimeSeconds);
            }}
            value={
              value == null
                ? moment.utc(0)
                : moment.utc(((value + OFFSET_SECONDS) % (60 * 60 * 24)) * 1000)
            }
          />
        </Col>
      </Row>
    );
  }

  if (expressionType.secondOperandType === RuleExpressionValueTypeEnum.NUMBER) {
    return <InputNumber className="w-full" onChange={onChange} value={value} />;
  }

  return <Input maxLength={100} onChange={(e) => onChange(e.target.value)} value={value} />;
}

export default RuleExpressionSecondOperandInput;
